import {graphql, PageProps} from 'gatsby'
import React from 'react'

import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import {HomepageV3Query} from '@/graphql-types'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'

import {SliceZone} from './slice-zone'

type Props = PageProps<HomepageV3Query>

export const HomepageV3: React.FC<Props> = (props) => {
    const data = props.data.prismicHomepageV3.data

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <SEO preventIndexing={data.exclude_robots} />

            <Header />

            <SliceZone slices={data.body} />

            <Footer />
        </>
    )
}

export const query = graphql`
    query HomepageV3($locale: String!) {
        prismicHomepageV3(lang: {eq: $locale}) {
            _previewable
            data {
                exclude_robots
                body {
                    ...FeatureBlock
                    ...GetStarted
                    ...Statistics
                    ...CustomerLogosPlusCta
                    ...RotatingBanner
                    ...VideoTabs
                    ...VideoWithOutline
                    ...Integrations
                    ...Hero
                }
            }
        }
    }

    fragment Integrations on PrismicHomepageV3DataBodyIntegrations {
        slice_type
        primary {
            title
            cta_text
            cta_link {
                ...PrismicLink
            }
            more_text
            logo_1 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_2 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_3 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment CustomerLogosPlusCta on PrismicHomepageV3DataBodyCustomerLogosPlusCta {
        slice_type
        primary {
            title
            cta_text
            cta_link {
                ...PrismicLink
            }
        }
        items {
            logo {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment VideoTabs on PrismicHomepageV3DataBodyVideoTabs {
        slice_type
        items {
            icon {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            title
            description
            poster {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
        }
    }

    fragment VideoWithOutline on PrismicHomepageV3DataBodyVideoWithOutline {
        slice_type
        primary {
            title
        }
        items {
            description
            name_title
            video_id
            thumbnail {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            customer_logo {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment RotatingBanner on PrismicHomepageV3DataBodyRotatingBanner {
        slice_type
        items {
            text
        }
    }

    fragment SignupReason on PrismicSignupReason {
        data {
            primary
            secondary
            icon {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment Hero on PrismicHomepageV3DataBodyHero {
        slice_type
        primary {
            section_html_id
            title
            inverse
            lead_text {
                raw
            }
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            wistiaVideoId: wistia_video_id
            rotating_signup_reasons
            signup_reason_1 {
                document {
                    ...SignupReason
                }
            }
            signup_reason_2 {
                document {
                    ...SignupReason
                }
            }
            signup_reason_3 {
                document {
                    ...SignupReason
                }
            }
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
        }
    }

    fragment Statistics on PrismicHomepageV3DataBodyStats {
        slice_type
        primary {
            first_stat_amount
            first_stat_text
            first_stat_as_percentile
            second_stat_amount
            second_stat_text
            second_stat_as_percentile
            third_stat_amount
            third_stat_text
            third_stat_as_percentile
            source_text {
                raw
            }
            predefined_statistics {
                document {
                    ... on PrismicStatisticsSection {
                        data {
                            first_stat {
                                amount
                                text
                                as_percentile
                            }
                            second_stat {
                                amount
                                text
                                as_percentile
                            }
                            third_stat {
                                amount
                                text
                                as_percentile
                            }
                            source_text {
                                raw
                            }
                        }
                    }
                }
            }
        }
    }

    fragment GetStarted on PrismicHomepageV3DataBodyGetStarted {
        slice_type
        primary {
            title
            error_message
            button_label
            input_placeholder
            form_subtitle
            aria_label
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
        }
    }

    fragment FeatureBlock on PrismicHomepageV3DataBodyFeatureBlock {
        slice_type
        primary {
            title_over_section
            title
            inverse
            background
            headline
            description {
                raw
            }
            pre_cta_links {
                raw
            }
            cta_label
            predefined_feature_block {
                document {
                    ... on PrismicFeatureBlock {
                        data {
                            image {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                            }
                            title
                            subtitle
                            body {
                                raw
                            }
                        }
                    }
                }
            }
            cta_link {
                ...PrismicLink
            }
            cta_hash_link
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            wistia_video_id
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
        }
    }
`

export default withPrismicPreview(HomepageV3)
